import React, { useState, useEffect, useRef, useCallback, memo, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import io from 'socket.io-client';
import DOMPurify from 'dompurify';
import twemoji from 'twemoji';
import EmojiPicker from 'emoji-picker-react';
import { 
  FaSearch, 
  FaPaperPlane, 
  FaEllipsisV, 
  FaUser, 
  FaCheck, 
  FaCheckDouble, 
  FaFile, 
  FaPaperclip,
  FaSmile,
  FaSpinner,
  FaArrowLeft,
  FaDownload,
  FaImage,
  FaCircle,
  FaMicrophone,
  FaTrashAlt,
  FaEllipsisH,
  FaPlay,
  FaPause,
  FaTrash,
  FaPhoneAlt,
  FaVideo,
  FaMicrophoneSlash,
  FaVideoSlash,
  FaPhone,
  FaVolumeUp,
  FaVolumeMute,
  FaDesktop
} from "react-icons/fa";

import "./EmployeeMessage.css";
import API_BASE_URL from '../../utils/apiConfig';

// Socket connection - Use environment-based URL
const ENDPOINT = process.env.REACT_APP_API_URL ? 
  process.env.REACT_APP_API_URL.replace('/api', '') : 
  "http://localhost:4000";
// Memoized socket connection
const getSocket = () => {
  if (!window.__socket) {
    window.__socket = io(ENDPOINT, {
      withCredentials: true,
      transports: ['websocket', 'polling']
    });
  }
  return window.__socket;
};

// Debounce utility
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Configure twemoji for better performance
const parseTwemoji = (text) => {
  if (!text) return '';
  
  // Use cache for parsed emoji html content
  if (window.__emojiCache === undefined) {
    window.__emojiCache = new Map();
  }
  
  // Return cached version if available
  if (window.__emojiCache.has(text)) {
    return window.__emojiCache.get(text);
  }
  
  // Parse and cache for future use - Updated configuration
  const parsed = twemoji.parse(text, {
    folder: 'svg',
    ext: '.svg',
    className: 'emoji-icon',
    size: '72x72',
    base: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/',
    callback: function(icon, options, variant) {
      switch (icon) {
        case 'a9':      // © copyright
        case 'ae':      // ® registered trademark
        case '2122':    // ™ trademark
          return false;
      }
      return ''.concat(options.base, options.size, '/', icon, options.ext);
    }
  });
  
  window.__emojiCache.set(text, parsed);
  return parsed;
};

// Memoized message bubble component
const MessageBubble = memo(({ message, isOwn, showSenderInfo, senderName, formatTimestamp, onContextMenu }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);
  
  // Memoized content to prevent re-renders
  const sanitizedContent = useMemo(() => {
    if (!message.content || message.isDeleted) return null;
    return DOMPurify.sanitize(parseTwemoji(message.content));
  }, [message.content, message.isDeleted]);
  
  // Audio event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleDurationChange = () => setDuration(audio.duration || 0);
    const handleEnded = () => setIsPlaying(false);
    
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);
    
    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [message.attachment?.url]);
  
  const formatAudioTime = useCallback((time) => {
    if (!time || isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60).toString().padStart(2, '0');
    const seconds = Math.floor(time % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  }, []);
  
  const togglePlay = useCallback((e) => {
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio) return;
    
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);
  
  return (
    <div 
      className={`employee-message-message ${isOwn ? "employee-message-message-own" : "employee-message-message-other"}`}
      onContextMenu={(e) => onContextMenu(e, message)}
    >
      <div className={`employee-message-bubble ${isOwn ? "employee-message-bubble-own" : "employee-message-bubble-other"}`}>
        {!isOwn && showSenderInfo && (
          <div className="employee-message-sender-name">
            {senderName}
          </div>
        )}
        
        <div className="employee-message-content">
          {message.isDeleted ? (
            <span className="employee-message-deleted-message">This message was deleted</span>
          ) : (
            <>
              {message.content && (
                <span 
                  dangerouslySetInnerHTML={{ __html: sanitizedContent }} 
                  className="employee-message-text-content"
                />
              )}
              
              {message.attachment && (
                <div className="employee-message-attachment">
                  {message.attachment.isVoiceMessage || 
                   (message.attachment.type && message.attachment.type.startsWith("audio")) ? (
                    <div className="employee-message-voice-message">
                      <button 
                        className={`employee-message-voice-play-btn ${isPlaying ? 'playing' : ''}`}
                        onClick={togglePlay}
                      >
                        {isPlaying ? <FaPause /> : <FaPlay />}
                      </button>
                      
                      <div className="employee-message-voice-waveform">
                        <div className="employee-message-voice-progress" style={{ 
                          width: `${duration ? (currentTime / duration) * 100 : 0}%` 
                        }}></div>
                      </div>
                      
                      <div className="employee-message-voice-time">
                        {formatAudioTime(duration - currentTime)}
                      </div>
                      
                      <audio 
                        ref={audioRef} 
                        src={message.attachment.url} 
                        preload="metadata" 
                        style={{ display: 'none' }}
                      />
                    </div>
                  ) : message.attachment.type && message.attachment.type.startsWith("image") ? (
                    <div className="employee-message-image-attachment">
                      <img 
                        src={message.attachment.url} 
                        alt="Attachment" 
                        loading="lazy"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "https://via.placeholder.com/300x200?text=Image+Not+Available";
                        }}
                      />
                      <div className="employee-message-attachment-info">
                        <span>{message.attachment.filename || "Image"}</span>
                        <a 
                          href={message.attachment.url} 
                          download 
                          target="_blank" 
                          rel="noreferrer" 
                          className="employee-message-download-btn"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <FaDownload />
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div className="employee-message-file-attachment">
                      <div className="employee-message-file-icon">
                        <FaFile />
                      </div>
                      <div className="employee-message-file-details">
                        <span className="employee-message-file-name">{message.attachment.filename || "File"}</span>
                        <a 
                          href={message.attachment.url} 
                          download 
                          target="_blank" 
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <FaDownload />
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          
          <div className="employee-message-meta">
            <span className="employee-message-time">{formatTimestamp(message.createdAt)}</span>
            {isOwn && (
              <span className="employee-message-status">
                {message.read ? <FaCheckDouble /> : <FaCheck />}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.message._id === nextProps.message._id &&
    prevProps.message.content === nextProps.message.content &&
    prevProps.message.isDeleted === nextProps.message.isDeleted &&
    prevProps.message.read === nextProps.message.read &&
    prevProps.isOwn === nextProps.isOwn &&
    prevProps.showSenderInfo === nextProps.showSenderInfo
  );
});

const EmployeeMessage = ({ darkMode }) => {
  const navigate = useNavigate();
  
  // Core state
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  
  // UI state
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const [showConversations, setShowConversations] = useState(true);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [showUploadOptions, setShowUploadOptions] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [mobileView, setMobileView] = useState(window.innerWidth <= 768);
  
  // Upload state
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [stagedAttachment, setStagedAttachment] = useState(null);
  const [attachmentCaption, setAttachmentCaption] = useState("");
  const [showAttachmentPreview, setShowAttachmentPreview] = useState(false);
  
  // Menu state
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showMessageMenu, setShowMessageMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [showDeleteMessageDialog, setShowDeleteMessageDialog] = useState(false);
  const [deleteOption, setDeleteOption] = useState(null);
  const [showDeleteConversationDialog, setShowDeleteConversationDialog] = useState(false);
  
  // Online status
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioRecorder, setAudioRecorder] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioPlayback, setAudioPlayback] = useState(null);
  
  // Call state
  const [callStatus, setCallStatus] = useState(null);
  const [isIncomingCall, setIsIncomingCall] = useState(false);
  const [isMakingCall, setIsMakingCall] = useState(false);
  const [isCallAccepted, setIsCallAccepted] = useState(false);
  const [isVideoCall, setIsVideoCall] = useState(false);
  const [callerId, setCallerId] = useState(null);
  const [callerName, setCallerName] = useState(null);
  const [callerAvatar, setCallerAvatar] = useState(null);
  const [calleeId, setCalleeId] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  const [callDuration, setCallDuration] = useState(0);
  const [callTimer, setCallTimer] = useState(null);
  const [callHistory, setCallHistory] = useState([]);
  const [screenSharing, setScreenSharing] = useState(false);
  
  // Refs
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const previousConversation = useRef(null);
  const socketRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const screenStreamRef = useRef(null);
  
  // Stable references
  const messageRefs = useRef(new Map());
  const conversationRefs = useRef(new Map());
  
  // Memoized values
  const filteredConversations = useMemo(() => {
    if (!searchTerm.trim()) return conversations;
    
    return conversations.filter(conv => {
      if (!currentUser) return true;
      const otherParticipant = getOtherParticipant(conv);
      return otherParticipant?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [conversations, searchTerm, currentUser]);
  
  // Debounced functions
  const debouncedSearch = useMemo(
    () => debounce((value) => setSearchTerm(value), 300),
    []
  );
  
  const debouncedScrollToBottom = useMemo(
    () => debounce(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 100),
    []
  );
  
  // Get other participant
  const getOtherParticipant = useCallback((conversation) => {
    if (!conversation?.participants || !currentUser?._id) {
      return { name: "Unknown", _id: null };
    }
    
    return conversation.participants.find(p => 
      p?._id && p._id !== currentUser._id
    ) || { name: "Unknown", _id: null };
  }, [currentUser?._id]);
  
  // Check if user is online
  const isUserOnline = useCallback((userId) => {
    return userId ? onlineUsers.has(userId) : false;
  }, [onlineUsers]);
  
  // Check if message is own
  const isOwnMessage = useCallback((message) => {
    if (!currentUser?._id || !message) return false;
    
    if (message.isTemp) return true;
    
    const senderId = message.sender?._id || message.sender;
    return senderId === currentUser._id;
  }, [currentUser?._id]);
  
  // Format timestamp
  const formatTimestamp = useCallback((timestamp) => {
    if (!timestamp) return "";
    
    const date = new Date(timestamp);
    const today = new Date();
    
    if (date.toDateString() === today.toDateString()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    
    if (today.getTime() - date.getTime() < 7 * 24 * 60 * 60 * 1000) {
      return date.toLocaleDateString([], { weekday: 'short' });
    }
    
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }, []);
  
  // Socket initialization
  useEffect(() => {
    socketRef.current = getSocket();
    
    const fetchCurrentUser = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/auth/profile`, {
          withCredentials: true
        });
        
        if (response.data.success) {
          setCurrentUser(response.data.user);
          socketRef.current?.emit('user_online', response.data.user._id);
        }
      } catch (error) {
        console.error("Error fetching current user:", error);
        navigate('/employee-login');
      }
    };

    fetchCurrentUser();
    
    return () => {
      if (window.__socket && window.__socket.connected) {
        window.__socket.disconnect();
        window.__socket = null;
      }
    };
  }, [navigate]);
  
  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/messages/conversations`, {
        withCredentials: true
      });
      
      if (response.data.success) {
        setConversations(response.data.conversations || []);
        
        // Calculate unread counts
        const counts = {};
        response.data.conversations?.forEach(conv => {
          counts[conv._id] = conv.unreadCount || 0;
        });
        setUnreadCounts(counts);
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);
  
  // Fetch messages
  const fetchMessages = useCallback(async (conversationId, pageNum = 1, append = false) => {
    if (!conversationId) return;
    
    try {
      setLoadingMore(true);
      
      const response = await axios.get(
        `${API_BASE_URL}/messages/${conversationId}?page=${pageNum}&limit=20`, 
        { withCredentials: true }
      );
      
      if (response.data.success) {
        const newMessages = response.data.messages || [];
        
        if (append) {
          setMessages(prev => [...newMessages.reverse(), ...prev]);
          setPage(pageNum);
        } else {
          setMessages(newMessages.reverse());
          setPage(1);
        }
        
        setHasMore(newMessages.length === 20);
        
        // Mark as read
        if (!append && conversationId) {
          markConversationAsRead(conversationId);
        }
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoadingMore(false);
    }
  }, []);
  
  // Mark conversation as read
  const markConversationAsRead = useCallback(async (conversationId) => {
    try {
      await axios.put(
        `${API_BASE_URL}/messages/mark-conversation-read/${conversationId}`,
        {},
        { withCredentials: true }
      );
      
      setUnreadCounts(prev => ({
        ...prev,
        [conversationId]: 0
      }));
    } catch (error) {
      console.error("Error marking conversation as read:", error);
    }
  }, []);
  
  // Update conversation list
  const updateConversationList = useCallback((newMessage) => {
    setConversations(prev => {
      const existing = prev.find(conv => conv._id === newMessage.conversationId);
      
      if (!existing) {
        fetchConversations();
        return prev;
      }
      
      const updated = prev.map(conv => {
        if (conv._id === newMessage.conversationId) {
          return {
            ...conv,
            lastMessage: newMessage,
            updatedAt: newMessage.createdAt
          };
        }
        return conv;
      });
      
      return updated.sort((a, b) => {
        const aTime = a.lastMessage?.createdAt || a.updatedAt || 0;
        const bTime = b.lastMessage?.createdAt || b.updatedAt || 0;
        return new Date(bTime).getTime() - new Date(aTime).getTime();
      });
    });
  }, [fetchConversations]);
  
  // Socket event handlers
  useEffect(() => {
    if (!socketRef.current || !currentUser) return;
    
    const handleNewMessage = (message) => {
      const isForActiveConversation = activeConversation && 
        message.conversationId === activeConversation._id;
      
      if (isForActiveConversation) {
        setMessages(prev => {
          const isDuplicate = prev.some(m => 
            m._id === message._id ||
            (m.tempId && m.tempId === message._id) ||
            (m.isTemp && m.content === message.content && 
             Math.abs(new Date(m.createdAt).getTime() - new Date(message.createdAt).getTime()) < 1000)
          );
          
          if (isDuplicate) return prev;
          
          return [...prev, { ...message, isNew: true }];
        });
        
        // Mark as read if message is from current user
        const isFromCurrentUser = message.sender?._id === currentUser._id ||
          message.sender === currentUser._id;
        
        if (!isFromCurrentUser) {
          markConversationAsRead(message.conversationId);
        }
      } else {
        // Update unread count for other conversations
        const isFromCurrentUser = message.sender?._id === currentUser._id ||
          message.sender === currentUser._id;
        
        if (!isFromCurrentUser) {
          setUnreadCounts(prev => ({
            ...prev,
            [message.conversationId]: (prev[message.conversationId] || 0) + 1
          }));
        }
      }
      
      updateConversationList(message);
    };
    
    const handleMessageDeleted = ({ messageId, deleteFor }) => {
      if (deleteFor === "everyone") {
        setMessages(prev => 
          prev.map(msg => 
            msg._id === messageId ? 
              { ...msg, isDeleted: true, content: "This message was deleted", attachment: null } : 
              msg
          )
        );
      }
    };
    
    const handleOnlineUsers = (userIds) => {
      setOnlineUsers(new Set(userIds));
    };
    
    socketRef.current.on("new_message", handleNewMessage);
    socketRef.current.on("message_deleted", handleMessageDeleted);
    socketRef.current.on("online_users", handleOnlineUsers);
    
    return () => {
      socketRef.current?.off("new_message", handleNewMessage);
      socketRef.current?.off("message_deleted", handleMessageDeleted);
      socketRef.current?.off("online_users", handleOnlineUsers);
    };
  }, [activeConversation, currentUser, updateConversationList, markConversationAsRead]);
  
  // Join/leave conversation rooms
  useEffect(() => {
    if (!socketRef.current || !activeConversation) return;
    
    const conversationId = activeConversation._id;
    
    if (previousConversation.current && 
        previousConversation.current !== conversationId) {
      socketRef.current.emit('leave_conversation', previousConversation.current);
    }
    
    socketRef.current.emit('join_conversation', conversationId);
    previousConversation.current = conversationId;
    
    return () => {
      if (socketRef.current && conversationId) {
        socketRef.current.emit('leave_conversation', conversationId);
      }
    };
  }, [activeConversation]);
  
  // Initialize
  useEffect(() => {
    if (currentUser) {
      fetchConversations();
    }
  }, [currentUser, fetchConversations]);
  
  // Mobile view handling
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setMobileView(mobile);
      
      if (!mobile) {
        setShowConversations(true);
      }
    };
    
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  
  // Scroll to bottom when messages change
  useEffect(() => {
    debouncedScrollToBottom();
  }, [messages, debouncedScrollToBottom]);
  
  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showMessageMenu && 
          !e.target.closest('.employee-message-context-menu')) {
        setShowMessageMenu(false);
      }
      
      if (showEmojiPicker && 
          emojiPickerRef.current && 
          !emojiPickerRef.current.contains(e.target) && 
          !e.target.closest('.employee-message-emoji')) {
        setShowEmojiPicker(false);
      }
      
      if (showUploadOptions && 
          !e.target.closest('.employee-message-upload-options') &&
          !e.target.closest('.employee-message-attach')) {
        setShowUploadOptions(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showMessageMenu, showEmojiPicker, showUploadOptions]);
  
  // Handle conversation selection
  const handleSelectConversation = useCallback((conversation) => {
    setActiveConversation(conversation);
    setPage(1);
    fetchMessages(conversation._id, 1);
    
    if (mobileView) {
      setShowConversations(false);
    }
    
    markConversationAsRead(conversation._id);
  }, [fetchMessages, mobileView, markConversationAsRead]);
  
  // Handle load more
  const handleLoadMore = useCallback(() => {
    if (loadingMore || !hasMore || !activeConversation) return;
    fetchMessages(activeConversation._id, page + 1, true);
  }, [loadingMore, hasMore, activeConversation, fetchMessages, page]);
  
  // Handle send message
  const handleSendMessage = useCallback(async () => {
    if (!newMessage.trim() || !activeConversation) return;
    
    const tempId = `temp-${Date.now()}`;
    const currentTimestamp = new Date().toISOString();
    
    const tempMessage = {
      _id: tempId,
      tempId,
      content: newMessage.trim(),
      createdAt: currentTimestamp,
      sender: currentUser,
      read: false,
      isTemp: true,
      conversationId: activeConversation._id
    };
    
    const messageContent = newMessage.trim();
    setNewMessage("");
    
    setMessages(prev => [...prev, tempMessage]);
    updateConversationList(tempMessage);
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/messages`, 
        {
          content: messageContent,
          conversationId: activeConversation._id,
          receiverId: getOtherParticipant(activeConversation)._id
        },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        const realMessage = {
          ...response.data.message,
          conversationId: activeConversation._id
        };
        
        setMessages(prev => 
          prev.map(msg => (msg._id === tempId || msg.tempId === tempId) ? realMessage : msg)
        );
        
        updateConversationList(realMessage);
        
        if (socketRef.current) {
          socketRef.current.emit("send_message", realMessage);
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages(prev => prev.filter(msg => msg._id !== tempId && msg.tempId !== tempId));
      setNewMessage(messageContent);
    }
  }, [newMessage, activeConversation, currentUser, updateConversationList, getOtherParticipant]);
  
  // Handle search
  const handleSearch = useCallback((e) => {
    debouncedSearch(e.target.value);
  }, [debouncedSearch]);
  
  // Handle file selection (first step - just preview)
  const handleFileSelection = (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      alert("File is too large. Maximum allowed size is 5MB.");
      return;
    }

    // Validate file type
    const allowedImageTypes = ['image/jpeg', 'image/png', 'image/gif'];
    const allowedDocTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
    
    if (type === 'image' && !allowedImageTypes.includes(file.type)) {
      alert("Only JPEG, PNG and GIF images are allowed.");
      return;
    } else if (type === 'file' && !allowedDocTypes.includes(file.type)) {
      alert("Only PDF, DOC, DOCX, and TXT files are allowed.");
      return;
    }

    // Create object URL for preview
    const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;

    // Stage the attachment for preview
    setStagedAttachment({
      file,
      type: file.type,
      filename: file.name,
      previewUrl,
      fileType: type
    });
    
    setShowAttachmentPreview(true);
    setShowUploadOptions(false);
  };

  // Handle sending attachment
  const handleSendAttachment = async () => {
    if (!stagedAttachment || !activeConversation) return;
    
    const file = stagedAttachment.file;
    const tempId = `temp-${Date.now()}`;
    const currentTimestamp = new Date().toISOString();
    
    setIsUploading(true);
    setUploadProgress(0);

    // Create temporary message for immediate display
    const tempAttachment = {
      type: file.type,
      filename: file.name,
      url: stagedAttachment.previewUrl
    };
    
    const tempMessage = {
      _id: tempId,
      tempId, 
      content: attachmentCaption || '',
      attachment: tempAttachment,
      createdAt: currentTimestamp,
      sender: currentUser,
      read: false,
      isTemp: true,
      conversationId: activeConversation._id
    };
    
    // Add to messages for immediate display
    setMessages(prev => [...prev, tempMessage]);
    
    // Update the conversation list immediately
    updateConversationList(tempMessage);
    
    // Hide attachment preview
    setShowAttachmentPreview(false);
    
    // Create form data for upload
    const formData = new FormData();
    formData.append('file', file);
    formData.append('conversationId', activeConversation._id);
    formData.append('receiverId', getOtherParticipant(activeConversation)._id);
    
    if (attachmentCaption) {
      formData.append('content', attachmentCaption);
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/messages/attachment`,
        formData,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        }
      );
      
      if (response.data.success) {
        // Add conversationId to the real message
        const realMessage = {
          ...response.data.message,
          conversationId: activeConversation._id
        };
        
        // Replace the temp message with the real one
        setMessages(prev => 
          prev.map(msg => (msg._id === tempId || msg.tempId === tempId) ? realMessage : msg)
        );
        
        // Update the conversation list with the real message
        updateConversationList(realMessage);
        
        // Clean up any blob URLs
        if (stagedAttachment.previewUrl) {
          URL.revokeObjectURL(stagedAttachment.previewUrl);
        }
        
        // Emit the message through socket
        if (socketRef.current) {
          socketRef.current.emit("send_message", realMessage);
        }
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Failed to upload file. Please try again.");
      
      // Remove the temp message
      setMessages(prev => prev.filter(msg => msg._id !== tempId && msg.tempId !== tempId));
      
      // Refresh conversation list to revert the temporary update
      fetchConversations();
    } finally {
      setIsUploading(false);
      setStagedAttachment(null);
      setAttachmentCaption("");
    }
  };

  // Start recording audio
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];
      
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };
      
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        setAudioBlob(blob);
        
        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop());
      };
      
      // Start recording
      recorder.start();
      setAudioRecorder(recorder);
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Error starting recording:", error);
      alert("Could not access microphone. Please check your permissions.");
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (audioRecorder && audioRecorder.state === "recording") {
      audioRecorder.stop();
      clearInterval(recordingTimerRef.current);
      setIsRecording(false);
    }
  };

  // Cancel recording
  const cancelRecording = () => {
    if (audioRecorder && audioRecorder.state === "recording") {
      audioRecorder.stop();
      clearInterval(recordingTimerRef.current);
      setIsRecording(false);
      setAudioBlob(null);
    }
  };

  // Send voice message
  const sendVoiceMessage = async () => {
    if (!audioBlob || !activeConversation) return;
    
    const tempId = `temp-${Date.now()}`;
    const currentTimestamp = new Date().toISOString();
    
    // Create a temp URL for immediate display
    const audioUrl = URL.createObjectURL(audioBlob);
    
    // Create temporary message for immediate display
    const tempAttachment = {
      type: 'audio/webm',
      filename: 'Voice message',
      url: audioUrl,
      isVoiceMessage: true
    };
    
    const tempMessage = {
      _id: tempId,
      tempId,
      content: '',
      attachment: tempAttachment,
      createdAt: currentTimestamp,
      sender: currentUser,
      read: false,
      isTemp: true,
      conversationId: activeConversation._id
    };
    
    // Add to messages for immediate display
    setMessages(prev => [...prev, tempMessage]);
    updateConversationList(tempMessage);
    
    // Reset audio state
    setAudioBlob(null);
    
    // Create form data for upload
    const formData = new FormData();
    formData.append('file', audioBlob, 'voice-message.webm');
    formData.append('conversationId', activeConversation._id);
    formData.append('receiverId', getOtherParticipant(activeConversation)._id);
    formData.append('messageType', 'voice');
    
    try {
      setIsUploading(true);
      setUploadProgress(0);
      
      const response = await axios.post(
        `${API_BASE_URL}/messages/attachment`,
        formData,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        }
      );
      
      if (response.data.success) {
        const realMessage = {
          ...response.data.message,
          conversationId: activeConversation._id
        };
        
        setMessages(prev => 
          prev.map(msg => (msg._id === tempId || msg.tempId === tempId) ? realMessage : msg)
        );
        
        updateConversationList(realMessage);
        
        if (socketRef.current) {
          socketRef.current.emit("send_message", realMessage);
        }
      }
    } catch (error) {
      console.error("Error sending voice message:", error);
      setMessages(prev => prev.filter (msg => msg._id !== tempId && msg.tempId !== tempId));
      fetchConversations();
    } finally {
      setIsUploading(false);
    }
  };

  // Format recording time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secs = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };
  
  // Handle context menu
  const handleMessageContextMenu = useCallback((e, message) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (message.isDeleting) return;
    
    const menuWidth = 180;
    const menuHeight = 100;
    
    let x = e.clientX;
    let y = e.clientY;
    
    if (x + menuWidth > window.innerWidth) {
      x = window.innerWidth - menuWidth - 10;
    }
    
    if (y + menuHeight > window.innerHeight) {
      y = window.innerHeight - menuHeight - 10;
    }
    
    setMenuPosition({ x, y });
    setSelectedMessage(message);
    setShowMessageMenu(true);
  }, []);
  
  // Handle delete message
  const handleDeleteMessage = useCallback(async (deleteFor = "me") => {
    if (!selectedMessage) return;
    
    try {
      setShowDeleteMessageDialog(false);
      
      const messageId = selectedMessage._id;
      
      if (deleteFor === "me") {
        setMessages(prev => prev.filter(msg => msg._id !== messageId));
      } else {
        setMessages(prev => 
          prev.map(msg => 
            msg._id === messageId ? 
              { ...msg, isDeleted: true, content: "This message was deleted", attachment: null } : 
              msg
          )
        );
      }
      
      const response = await axios.delete(
        `${API_BASE_URL}/messages/message/${messageId}?deleteFor=${deleteFor}`,
        { withCredentials: true }
      );
      
      if (response.data.success && deleteFor === "everyone" && socketRef.current) {
        socketRef.current.emit("delete_message", {
          messageId,
          conversationId: activeConversation._id,
          deleteFor
        });
      }
    } catch (error) {
      console.error("Error deleting message:", error);
      if (activeConversation) {
        fetchMessages(activeConversation._id);
      }
    } finally {
      setShowMessageMenu(false);
      setSelectedMessage(null);
    }
  }, [selectedMessage, activeConversation, fetchMessages]);
  
  // Handle delete conversation
  const handleDeleteConversation = useCallback(async () => {
    if (!activeConversation) return;
    
    try {
      setShowDeleteConversationDialog(false);
      
      const conversationId = activeConversation._id;
      const currentConversations = [...conversations];
      
      setConversations(prev => prev.filter(conv => conv._id !== conversationId));
      
      const remaining = currentConversations.filter(c => c._id !== conversationId);
      if (remaining.length > 0) {
        handleSelectConversation(remaining[0]);
      } else {
        setActiveConversation(null);
        setMessages([]);
      }
      
      const response = await axios.delete(
        `${API_BASE_URL}/messages/conversation/${conversationId}`,
        { withCredentials: true }
      );
      
      if (!response.data.success) {
        setConversations(currentConversations);
        alert(`Failed to delete conversation: ${response.data.message}`);
      }
    } catch (error) {
      console.error("Error deleting conversation:", error);
      fetchConversations();
    }
  }, [activeConversation, conversations, handleSelectConversation, fetchConversations]);
  
  // Handle emoji selection
  const handleEmojiClick = useCallback((emojiData) => {
    const emoji = emojiData.emoji;
    
    // Use requestAnimationFrame for smoother UI updates
    window.requestAnimationFrame(() => {
      setNewMessage(prev => prev + emoji);
      
      // Preload emoji to avoid flickering on first appearance
      if (typeof twemoji !== 'undefined') {
        twemoji.parse(emoji, {
          folder: 'svg',
          ext: '.svg'
        });
      }
      
      // Focus back on input in the next frame
      window.requestAnimationFrame(() => {
        if (messageInputRef.current) {
          messageInputRef.current.focus();
        }
      });
    });
  }, []);
  
  // Add this useEffect to preload common emojis
  useEffect(() => {
    // Preload common emoji sets to improve first render performance
    const commonEmojis = ['😀', '😂', '👍', '❤️', '🙏', '👋', '😊', '🎉', '👏', '🔥',
                         '😁', '😄', '😆', '😅', '😂', '🤣', '😇', '😉', '😘', '🥰'];
    
    // Batch process to avoid blocking the main thread
    const preloadEmojis = (index = 0) => {
      if (index >= commonEmojis.length) return;
      
      const emoji = commonEmojis[index];
      if (typeof twemoji !== 'undefined') {
        twemoji.parse(emoji, {
          folder: 'svg',
          ext: '.svg'
        });
      }
      
      // Process next emoji in the next idle period
      if (window.requestIdleCallback) {
        window.requestIdleCallback(() => preloadEmojis(index + 1));
      } else {
        setTimeout(() => preloadEmojis(index + 1), 0);
      }
    };
    
    // Start preloading
    preloadEmojis();
  }, []);
  
  // Handle back to conversations
  const handleBackToConversations = useCallback(() => {
    if (mobileView) {
      setShowConversations(true);
    }
    setActiveConversation(null);
  }, [mobileView]);
  
  // Handle exit
  const handleExit = useCallback(() => {
    navigate('/employee-dashboard');
  }, [navigate]);
  
  // Render messages
  const renderMessages = useMemo(() => {
    let lastDate = null;
    let lastSenderId = null;
    
    return messages
      .filter(message => {
        const isDeletedForCurrentUser = message.deletedFor && 
          Array.isArray(message.deletedFor) && 
          message.deletedFor.some(id => 
            (typeof id === 'string' && id === currentUser?._id) || 
            (id?._id && id._id === currentUser?._id)
          );
        
        return !isDeletedForCurrentUser;
      })
      .map((message, index) => {
        const isOwn = isOwnMessage(message);
        const messageDate = new Date(message.createdAt).toDateString();
        const showDate = lastDate !== messageDate;
        if (showDate) lastDate = messageDate;
        
        const showSenderInfo = lastSenderId !== message.sender?._id;
        lastSenderId = message.sender?._id;
        
        const senderName = isOwn ? 'You' : (message.sender?.name || 'Unknown');
        
        return (
          <React.Fragment key={message._id || message.tempId || index}>
            {showDate && (
              <div className="employee-message-date-divider">
                <span>{new Date(message.createdAt).toLocaleDateString()}</span>
              </div>
            )}
            
            <MessageBubble
              message={message}
              isOwn={isOwn}
              showSenderInfo={showSenderInfo}
              senderName={senderName}
              formatTimestamp={formatTimestamp}
              onContextMenu={handleMessageContextMenu}
            />
          </React.Fragment>
        );
      });
  }, [messages, isOwnMessage, formatTimestamp, handleMessageContextMenu, currentUser]);
  
  return (
    <div className={`employee-message-container ${darkMode ? "employee-message-dark" : ""}`}>
      <div className={`employee-message-sidebar ${!showConversations && mobileView ? "employee-message-sidebar-hidden" : ""}`}>
        <div className="employee-message-header">
          <div className="employee-message-user-info">
            {currentUser?.profilePicture ? (
              <img 
                src={currentUser.profilePicture} 
                alt="Profile" 
                className="employee-message-profile-image" 
              />
            ) : (
              <div className="employee-message-default-avatar employee-message-header-avatar">
                {currentUser?.name ? currentUser.name.charAt(0).toUpperCase() : "U"}
              </div>
            )}
            <h2>Messages</h2>
          </div>
          <div className="employee-message-header-actions">
            <button className="employee-message-icon-button" onClick={handleExit}>
              <FaArrowLeft />
            </button>
          </div>
        </div>
        
        <div className="employee-message-search">
          <div className="employee-message-search-container">
            <FaSearch className="employee-message-search-icon" />
            <input 
              type="text" 
              placeholder="Search or start new chat" 
              onChange={handleSearch}
            />
          </div>
        </div>
        
        <div className="employee-message-chats">
          {loading ? (
            <div className="employee-message-loading">
              <FaSpinner className="employee-message-spinner" />
              <p>Loading conversations...</p>
            </div>
          ) : filteredConversations.length > 0 ? (
            filteredConversations.map(conversation => {
              const otherParticipant = getOtherParticipant(conversation);
              const isActive = activeConversation?._id === conversation._id;
              const unreadCount = unreadCounts[conversation._id] || 0;
              
              const getPreviewText = () => {
                if (!conversation.lastMessage) return "Start a conversation";
                
                if (conversation.lastMessage.attachment) {
                  if (conversation.lastMessage.attachment.type?.startsWith("image")) {
                    return "📷 Photo";
                  }
                  return "📎 Document";
                }
                
                if (!conversation.lastMessage.content) return "No message";
                
                return conversation.lastMessage.content.length > 40
                  ? `${conversation.lastMessage.content.substring(0, 40)}...`
                  : conversation.lastMessage.content;
              };
              
              return (
                <div 
                  key={conversation._id}
                  className={`employee-message-chat-item ${isActive ? "employee-message-active-chat" : ""}`}
                  onClick={() => handleSelectConversation(conversation)}
                >
                  <div className="employee-message-chat-avatar">
                    {otherParticipant.profilePicture ? (
                      <img src={otherParticipant.profilePicture} alt={otherParticipant.name} />
                    ) : (
                      <div className="employee-message-default-avatar">
                        {otherParticipant.name ? otherParticipant.name.charAt(0).toUpperCase() : "?"}
                      </div>
                    )}
                  </div>
                  
                  <div className="employee-message-chat-details">
                    <div className="employee-message-chat-header">
                      <h4>{otherParticipant.name}</h4>
                      <span className="employee-message-chat-time">
                        {conversation.lastMessage ? formatTimestamp(conversation.lastMessage.createdAt) : ""}
                      </span>
                    </div>
                    
                    <div className="employee-message-chat-message">
                      <p className={unreadCount > 0 ? "employee-message-unread-text" : ""}>
                        {getPreviewText()}
                      </p>
                      
                      {unreadCount > 0 && (
                        <div className="employee-message-unread-count">{unreadCount}</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="employee-message-empty">
              <p>No conversations found</p>
            </div>
          )}
        </div>
      </div>
      
      <div className={`employee-message-chat ${(!activeConversation || (showConversations && mobileView)) ? "employee-message-chat-hidden" : ""}`}>
        {activeConversation ? (
          <>
            <div className="employee-message-chat-header">
              {mobileView && (
                <button 
                  className="employee-message-back-button"
                  onClick={handleBackToConversations}
                >
                  <FaArrowLeft />
                </button>
              )}
              
              <div className="employee-message-chat-user">
                <div className="employee-message-chat-avatar">
                  {getOtherParticipant(activeConversation)?.profilePicture ? (
                    <img 
                      src={getOtherParticipant(activeConversation).profilePicture} 
                      alt={getOtherParticipant(activeConversation)?.name || "User"} 
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://via.placeholder.com/40?text=?";
                      }}
                    />
                  ) : (
                    <div className="employee-message-default-avatar">
                      {getOtherParticipant(activeConversation)?.name?.charAt(0).toUpperCase() || "?"}
                    </div>
                  )}
                </div>
                
                <div className="employee-message-chat-user-info">
                  <h3>{getOtherParticipant(activeConversation).name}</h3>
                  <span className={`employee-message-user-status ${isUserOnline(getOtherParticipant(activeConversation)._id) ? "employee-message-status-online" : ""}`}>
                    {isUserOnline(getOtherParticipant(activeConversation)._id) ? "Online" : "Offline"}
                  </span>
                </div>
              </div>
              
              <div className="employee-message-chat-actions">
                <button className="employee-message-icon-button">
                  <FaPhoneAlt />
                </button>
                <button className="employee-message-icon-button">
                  <FaVideo />
                </button>
                <button className="employee-message-icon-button">
                  <FaSearch />
                </button>
                <div className="employee-message-dropdown">
                  <button className="employee-message-icon-button">
                    <FaEllipsisV />
                  </button>
                  <div className="employee-message-dropdown-menu">
                    <button 
                      className="employee-message-dropdown-item"
                      onClick={() => setShowDeleteConversationDialog(true)}
                    >
                      <FaTrashAlt /> Delete Chat
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="employee-message-chat-body" ref={messagesContainerRef}>
              {hasMore && (
                <div className="employee-message-load-more">
                  <button onClick={handleLoadMore} disabled={loadingMore}>
                    {loadingMore ? (
                      <>
                        <FaSpinner className="employee-message-spinner" />
                        Loading...
                      </>
                    ) : (
                      "Load older messages"
                    )}
                  </button>
                </div>
              )}
              
              <div className="employee-message-messages">
                {messages.length > 0 ? renderMessages : (
                  <div className="employee-message-no-messages">
                    <p>No messages yet. Start the conversation!</p>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>
            
            <div className="employee-message-chat-footer">
              <button 
                className={`employee-message-icon-button employee-message-emoji ${showEmojiPicker ? 'active' : ''}`}
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              >
                <FaSmile />
              </button>
              
              {showEmojiPicker && (
                <div className="employee-message-emoji-picker" ref={emojiPickerRef}>
                  <EmojiPicker
                    onEmojiClick={handleEmojiClick}
                    searchPlaceholder="Search emoji"
                    width={300}
                    height={400}
                    previewConfig={{ showPreview: false }}
                    lazyLoadEmojis={true}
                    skinTonesDisabled
                    autoFocusSearch={false}
                  />
                </div>
              )}
              
              <button 
                className="employee-message-icon-button employee-message-attach"
                onClick={() => setShowUploadOptions(!showUploadOptions)}
              >
                <FaPaperclip />
              </button>
              
              <div className="employee-message-input-container">
                <input
                  type="text"
                  placeholder="Type a message"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                  ref={messageInputRef}
                />
              </div>
              
              {isRecording ? (
                <div className="employee-message-recording">
                  <div className="employee-message-recording-indicator">
                    <span className="recording-dot"></span>
                    <span className="recording-time">{formatTime(recordingTime)}</span>
                  </div>
                  <div className="employee-message-recording-actions">
                    <button 
                      className="employee-message-icon-button employee-message-cancel-recording"
                      onClick={cancelRecording}
                    >
                      <FaTrash />
                    </button>
                    <button 
                      className="employee-message-icon-button employee-message-send-recording"
                      onClick={stopRecording}
                    >
                      <FaCheck />
                    </button>
                  </div>
                </div>
              ) : audioBlob ? (
                <div className="employee-message-recording-preview">
                  <div className="employee-message-voice-message">
                    <button 
                      className="employee-message-voice-play-btn"
                      onClick={() => {
                        const audio = new Audio(URL.createObjectURL(audioBlob));
                        if (audioPlayback) {
                          audioPlayback.pause();
                        }
                        audio.play();
                        setAudioPlayback(audio);
                      }}
                    >
                      <FaPlay />
                    </button>
                    <div className="employee-message-voice-waveform"></div>
                    <div className="employee-message-recording-actions">
                      <button 
                        className="employee-message-icon-button employee-message-cancel-recording"
                        onClick={() => setAudioBlob(null)}
                      >
                        <FaTrash />
                      </button>
                      <button 
                        className="employee-message-icon-button employee-message-send-recording"
                        onClick={sendVoiceMessage}
                      >
                        <FaPaperPlane />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <button 
                  className="employee-message-icon-button employee-message-send"
                  onClick={newMessage.trim() ? handleSendMessage : startRecording}
                >
                  {newMessage.trim() ? <FaPaperPlane /> : <FaMicrophone />}
                </button>
              )}
            </div>

            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={(e) => handleFileSelection(e, 'file')}
              style={{ display: 'none' }}
              accept=".pdf,.doc,.docx,.txt,.zip,.rar"
            />

            <input 
              type="file" 
              ref={imageInputRef} 
              onChange={(e) => handleFileSelection(e, 'image')}
              style={{ display: 'none' }}
              accept="image/*"
            />

            {/* Upload options dropdown */}
            {showUploadOptions && (
              <div className="employee-message-upload-options">
                <button 
                  className="employee-message-upload-option"
                  onClick={() => imageInputRef.current.click()}
                >
                  <div className="employee-message-upload-icon employee-message-photo-icon">
                    <FaImage />
                  </div>
                  <span>Photo</span>
                </button>
                <button 
                  className="employee-message-upload-option"
                  onClick={() => fileInputRef.current.click()}
                >
                  <div className="employee-message-upload-icon employee-message-doc-icon">
                    <FaFile />
                  </div>
                  <span>Document</span>
                </button>
              </div>
            )}

            {/* Upload progress */}
            {isUploading && (
              <div className="employee-message-upload-progress">
                <div className="employee-message-progress-bar">
                  <div 
                    className="employee-message-progress-fill" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <span>{uploadProgress}%</span>
              </div>
            )}

            {/* Attachment Preview UI */}
            {showAttachmentPreview && stagedAttachment && (
              <div className="employee-message-attachment-preview">
                <div className="employee-message-attachment-preview-header">
                  <button 
                    className="employee-message-icon-button" 
                    onClick={() => {
                      setShowAttachmentPreview(false);
                      setStagedAttachment(null);
                      if (stagedAttachment.previewUrl) {
                        URL.revokeObjectURL(stagedAttachment.previewUrl);
                      }
                    }}
                  >
                    <FaArrowLeft />
                  </button>
                  <h3>{stagedAttachment.fileType === 'image' ? 'Preview Image' : 'Preview Document'}</h3>
                </div>
                
                <div className="employee-message-attachment-preview-content">
                  {stagedAttachment.type && stagedAttachment.type.startsWith("image") ? (
                    <img 
                      src={stagedAttachment.previewUrl} 
                      alt="Preview" 
                      className="employee-message-attachment-preview-image"
                    />
                  ) : (
                    <div className="employee-message-attachment-preview-document">
                      <div className="employee-message-file-icon large-icon">
                        <FaFile />
                      </div>
                      <p className="employee-message-file-name">{stagedAttachment.filename}</p>
                    </div>
                  )}
                </div>
                
                <div className="employee-message-attachment-caption">
                  <div className="employee-message-input-container">
                    <input
                      type="text"
                      placeholder="Add a caption..."
                      value={attachmentCaption}
                      onChange={(e) => setAttachmentCaption(e.target.value)}
                      autoFocus
                    />
                  </div>
                  
                  <button 
                    className="employee-message-icon-button employee-message-send"
                    onClick={handleSendAttachment}
                  >
                    <FaPaperPlane />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="employee-message-welcome">
            <div className="employee-message-welcome-container">
              <div className="employee-message-welcome-image"></div>
              <h1>Keep your phone connected</h1>
              <p>Select a chat to start messaging</p>
            </div>
          </div>
        )}
      </div>

      {/* Context Menu */}
      {showMessageMenu && selectedMessage && (
        <div 
          className="employee-message-context-menu"
          style={{ top: menuPosition.y, left: menuPosition.x }}
        >
          <button 
            className="employee-message-context-menu-item"
            onClick={() => {
              setDeleteOption("me");
              setShowDeleteMessageDialog(true);
              setShowMessageMenu(false);
            }}
          >
            <FaTrashAlt /> Delete For Me
          </button>
          {isOwnMessage(selectedMessage) && !selectedMessage.isDeleted && (
            <button 
              className="employee-message-context-menu-item"
              onClick={() => {
                setDeleteOption("everyone");
                setShowDeleteMessageDialog(true);
                setShowMessageMenu(false);
              }}
            >
              <FaTrashAlt /> Delete For Everyone
            </button>
          )}
        </div>
      )}

      {/* Delete Message Dialog */}
      {showDeleteMessageDialog && (
        <div className="employee-message-dialog-overlay">
          <div className="employee-message-dialog">
            <h3>{deleteOption === "everyone" ? "Delete For Everyone" : "Delete For Me"}</h3>
            <p>
              {deleteOption === "everyone" 
                ? "This message will be deleted for everyone in this chat." 
                : "This message will only be deleted for you."}
            </p>
            <div className="employee-message-dialog-actions">
              <button 
                className="employee-message-dialog-btn employee-message-cancel-btn"
                onClick={() => setShowDeleteMessageDialog(false)}
              >
                Cancel
              </button>
              <button 
                className="employee-message-dialog-btn employee-message-delete-btn"
                onClick={() => handleDeleteMessage(deleteOption || "me")}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Conversation Dialog */}
      {showDeleteConversationDialog && (
        <div className="employee-message-dialog-overlay">
          <div className="employee-message-dialog">
            <h3>Delete Conversation</h3>
            <p>Are you sure you want to delete this conversation?</p>
            <div className="employee-message-dialog-actions">
              <button 
                className="employee-message-dialog-btn employee-message-cancel-btn"
                onClick={() => setShowDeleteConversationDialog(false)}
              >
                Cancel
              </button>
              <button 
                className="employee-message-dialog-btn employee-message-delete-btn"
                onClick={handleDeleteConversation}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeMessage;